import React, { useEffect, useMemo, useState, memo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FileText, DollarSign, CheckCircle, AlertCircle } from 'lucide-react';
import api, { formatErrorMessage } from '../../../../utils/api';
import { toast } from 'sonner';

const ALCANCES = [
  { value: 'total_deuda', label: 'Toda la deuda', desc: 'El compromiso cubre el saldo total pendiente del RUT.' },
  { value: 'monto_parcial', label: 'Monto parcial', desc: 'Un abono parcial sin facturas específicas.' },
  { value: 'una_factura', label: 'Una factura', desc: 'Aplica a una factura específica.' },
  { value: 'varias_facturas', label: 'Varias facturas', desc: 'Aplica a un grupo de facturas seleccionadas.' },
];

const fmtCLP = (n) => new Intl.NumberFormat('es-CL').format(Math.round(Number(n || 0)));

/**
 * Modal para crear un compromiso de pago sobre una cuenta consolidada (RUT).
 * Alcances: total_deuda | monto_parcial | una_factura | varias_facturas.
 * El monto puede ser MENOR a la suma de facturas (pago parcial permitido).
 * Props:
 *  - open, onOpenChange
 *  - cuenta: {rut_normalizado, rut_display, nombre_principal, principal_client_id, saldo_total}
 *  - onSuccess: callback tras crear el compromiso
 *  - minutaId, minutaItemId, gestionOrigenId (opcionales, para trazabilidad)
 */
const NuevoCompromisoModal = memo(({ open, onOpenChange, cuenta, onSuccess, minutaId, minutaItemId, gestionOrigenId }) => {
  const [alcance, setAlcance] = useState('total_deuda');
  const [monto, setMonto] = useState('');
  const [fecha, setFecha] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [facturasSelId, setFacturasSelId] = useState('');
  const [facturasSelIds, setFacturasSelIds] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAlcance('total_deuda');
    setMonto('');
    setFecha('');
    setObservaciones('');
    setFacturasSelId('');
    setFacturasSelIds([]);
    if (cuenta?.rut_normalizado) {
      setLoadingDetalle(true);
      api.get(`/finanzas/cobranza/cuentas-consolidadas/${encodeURIComponent(cuenta.rut_normalizado)}/detalle-completo`)
        .then(r => setFacturas((r.data?.facturas || []).filter(f => f.status !== 'pagada')))
        .catch(err => toast.error(formatErrorMessage(err) || 'Error al cargar facturas'))
        .finally(() => setLoadingDetalle(false));
    }
  }, [open, cuenta]);

  const sumaFacturas = useMemo(() => {
    if (alcance === 'una_factura' && facturasSelId) {
      const f = facturas.find(x => x.id === facturasSelId);
      return f ? Number(f.total_amount_clp || 0) : 0;
    }
    if (alcance === 'varias_facturas') {
      return facturasSelIds.reduce((acc, id) => {
        const f = facturas.find(x => x.id === id);
        return acc + Number(f?.total_amount_clp || 0);
      }, 0);
    }
    return 0;
  }, [alcance, facturasSelId, facturasSelIds, facturas]);

  const toggleFactura = (id) => {
    setFacturasSelIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const puedeGuardar = () => {
    if (!cuenta?.principal_client_id) return false;
    if (!fecha) return false;
    const m = Number(monto);
    if (!m || m <= 0) return false;
    if (alcance === 'una_factura' && !facturasSelId) return false;
    if (alcance === 'varias_facturas' && facturasSelIds.length < 2) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!puedeGuardar()) {
      toast.error('Completa los campos obligatorios y el alcance seleccionado.');
      return;
    }
    setLoading(true);
    try {
      const body = {
        cliente_id: cuenta.principal_client_id,
        cliente_nombre: cuenta.nombre_principal,
        alcance,
        monto_comprometido: Number(monto),
        fecha_compromiso: fecha,
        observaciones: observaciones || null,
        gestion_origen_id: gestionOrigenId || null,
        minuta_id: minutaId || null,
        minuta_item_id: minutaItemId || null,
      };
      if (alcance === 'una_factura') body.factura_id = facturasSelId;
      if (alcance === 'varias_facturas') body.facturas_asociadas = facturasSelIds;
      const r = await api.post('/finanzas/cobranza/compromisos', body);
      toast.success('Compromiso de pago registrado');
      onOpenChange(false);
      onSuccess?.(r.data?.compromiso);
    } catch (err) {
      toast.error(formatErrorMessage(err) || 'Error al registrar compromiso');
    } finally {
      setLoading(false);
    }
  };

  const mostrarFacturas = alcance === 'una_factura' || alcance === 'varias_facturas';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto' data-testid='nuevo-compromiso-modal'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <FileText className='w-5 h-5 text-purple-600' />
            Nuevo Compromiso de Pago
          </DialogTitle>
          <DialogDescription>
            {cuenta ? <>Cuenta consolidada por RUT <span className='font-mono'>{cuenta.rut_display || cuenta.rut_normalizado}</span> · {cuenta.nombre_principal} · Saldo total: ${fmtCLP(cuenta.saldo_total)}</> : 'Seleccione una cuenta primero.'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          {/* Alcance */}
          <div>
            <Label className='text-sm font-semibold mb-2 block'>Alcance del compromiso</Label>
            <RadioGroup value={alcance} onValueChange={setAlcance}>
              <div className='grid grid-cols-2 gap-2'>
                {ALCANCES.map(a => (
                  <label
                    key={a.value}
                    className={`flex items-start gap-2 p-2 border rounded cursor-pointer hover:bg-slate-50 ${alcance === a.value ? 'border-purple-500 bg-purple-50' : 'border-slate-200'}`}
                    data-testid={`alcance-opt-${a.value}`}
                  >
                    <RadioGroupItem value={a.value} className='mt-1' />
                    <div className='flex-1'>
                      <p className='text-sm font-medium'>{a.label}</p>
                      <p className='text-xs text-slate-500'>{a.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Selector de facturas */}
          {mostrarFacturas && (
            <div className='p-3 bg-slate-50 border border-slate-200 rounded'>
              {alcance === 'una_factura' ? (
                <>
                  <Label className='text-xs'>Factura *</Label>
                  <Select value={facturasSelId} onValueChange={setFacturasSelId}>
                    <SelectTrigger data-testid='compromiso-factura-select' className='h-9'>
                      <SelectValue placeholder={loadingDetalle ? 'Cargando...' : 'Seleccione una factura'} />
                    </SelectTrigger>
                    <SelectContent>
                      {facturas.map(f => (
                        <SelectItem key={f.id} value={f.id}>#{f.numero_factura} · ${fmtCLP(f.total_amount_clp)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              ) : (
                <>
                  <Label className='text-xs'>Facturas (mínimo 2) *</Label>
                  <div className='max-h-48 overflow-y-auto border border-slate-200 rounded bg-white'>
                    {facturas.length === 0 ? (
                      <p className='p-3 text-xs text-slate-400 text-center'>{loadingDetalle ? 'Cargando facturas...' : 'Sin facturas pendientes'}</p>
                    ) : facturas.map(f => (
                      <label
                        key={f.id}
                        className='flex items-center gap-2 p-2 border-b border-slate-100 hover:bg-slate-50 cursor-pointer text-xs'
                        data-testid={`compromiso-factura-check-${f.id}`}
                      >
                        <Checkbox
                          checked={facturasSelIds.includes(f.id)}
                          onCheckedChange={() => toggleFactura(f.id)}
                        />
                        <span className='flex-1'>#{f.numero_factura}</span>
                        <span className='text-slate-500'>${fmtCLP(f.total_amount_clp)}</span>
                      </label>
                    ))}
                  </div>
                  <p className='text-[10px] text-slate-500 mt-1'>Seleccionadas: {facturasSelIds.length} · Suma: ${fmtCLP(sumaFacturas)}</p>
                </>
              )}
              {sumaFacturas > 0 && (
                <p className='text-[10px] text-slate-500 mt-1'>
                  Suma seleccionada: ${fmtCLP(sumaFacturas)}. El monto comprometido puede ser menor (pago parcial).
                </p>
              )}
            </div>
          )}

          <div className='grid grid-cols-2 gap-3'>
            <div>
              <Label className='text-xs'>Monto comprometido *</Label>
              <div className='relative'>
                <DollarSign className='w-4 h-4 text-slate-400 absolute left-2 top-2.5' />
                <Input
                  type='number'
                  min='0'
                  value={monto}
                  onChange={e => setMonto(e.target.value)}
                  placeholder='0'
                  className='pl-8'
                  data-testid='compromiso-monto'
                />
              </div>
            </div>
            <div>
              <Label className='text-xs'>Fecha prometida *</Label>
              <Input
                type='date'
                value={fecha}
                onChange={e => setFecha(e.target.value)}
                data-testid='compromiso-fecha'
              />
            </div>
          </div>

          <div>
            <Label className='text-xs'>Observaciones</Label>
            <Textarea
              value={observaciones}
              onChange={e => setObservaciones(e.target.value)}
              placeholder='Detalles del acuerdo con el cliente'
              rows={2}
              data-testid='compromiso-observaciones'
            />
          </div>

          {(minutaId || gestionOrigenId) && (
            <div className='p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-center gap-2'>
              <AlertCircle className='w-3 h-3' />
              {minutaId && <>Vinculado a minuta {minutaId?.slice(0, 8)}…</>}
              {gestionOrigenId && <>· gestión origen {gestionOrigenId?.slice(0, 8)}…</>}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !puedeGuardar()}
            data-testid='compromiso-submit'
          >
            <CheckCircle className='w-4 h-4 mr-2' />
            {loading ? 'Guardando...' : 'Registrar Compromiso'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default NuevoCompromisoModal;
